import {useInfiniteQuery} from '@tanstack/react-query';
import {useState, useSyncExternalStore} from 'react';
import {I18n} from '@aws-amplify/core';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912): nulls-last and numeric-vs-string live in the
// library, not in a per-module copy.
import {sortRecords} from '@flexinet/ui-components';

import {fetchRecords} from '../services/ReconciliationReportsService';
import {BillingChannel, ReconciliationReport, ReconciliationReportRow} from '../types.ts';
import {totalDiscrepancies} from '../utils/discrepancyHealth.ts';
import {useReconciliationReportsSearch} from './useReconciliationReportsSearch.ts';

// FNP-535: read-only module. No create/update/delete mutations — the ChannelReconciler
// (Job B) owns the lifecycle. This hook is list-fetch only.
//
// listReconciliationReports REQUIRES a channel (the channel-runStartedAt-index partition
// key), so the list is always scoped to exactly one channel. FNP-607: the channel is the
// module's DEFAULT FILTER (defaults to the canonical `stripe` token so the list works out of
// the box) and is surfaced through the standard Filter button/drawer/form like every other
// module — but it is REQUIRED and NON-CLEARABLE: there is always exactly one channel
// selected, so the store is a non-null BillingChannel and "Clear" resets to the default
// rather than to none. FNP-616: the token is the lowercase catalog key (the selectable set
// is the ENABLED BillingProviders); `stripe` is the live FNP-212 channel.
export const DEFAULT_CHANNEL: BillingChannel = 'stripe';

let globalChannel: BillingChannel = DEFAULT_CHANNEL;
const channelListeners: Set<() => void> = new Set();

const notifyChannelChange = (channel: BillingChannel) => {
    globalChannel = channel;
    channelListeners.forEach(listener => listener());
};

// External store wired through useSyncExternalStore (FNP-593): the previous
// useState(globalChannel) + useEffect-registered listener could miss a change
// fired between a consumer's first render and its effect commit, briefly
// desyncing the selector and the list. useSyncExternalStore subscribes during
// render and reads the live snapshot, so there is no race and no tearing.
const subscribeChannel = (listener: () => void) => {
    channelListeners.add(listener);
    return () => {
        channelListeners.delete(listener);
    };
};

const getChannelSnapshot = (): BillingChannel => globalChannel;

// Test-only escape hatch — resets the module-scoped store to its default. Tests
// share the store, so without this they leak state between cases.
export const __resetReconciliationReportsChannelForTesting = () => {
    notifyChannelChange(DEFAULT_CHANNEL);
};

export const useReconciliationReportsChannel = () => {
    const channel = useSyncExternalStore(subscribeChannel, getChannelSnapshot);

    const handleSetChannel = (newChannel: BillingChannel) => {
        notifyChannelChange(newChannel);
    };

    return {channel, handleSetChannel};
};

// Page size is a server concern fixed by the module (§9), never a user choice.
const PAGE_SIZE = 10;

// NEWEST RUN FIRST, on runStartedAt: `AWSDateTime!` (non-null, set once when the run
// starts) and the sort key of channel-runStartedAt-index, which the resolver reads with
// scanIndexForward false. So first paint agrees with the server's own order.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<ReconciliationReportRow> = {
    columnAccessor: 'runStartedAt',
    direction: 'desc',
};

export const reconciliationReportsKeys = {
    all: ['reconciliation-reports'] as const,
    list: (channel: BillingChannel) => ['reconciliation-reports', 'list', {channel}] as const,
    detail: (id?: string) => ['reconciliation-reports', 'detail', id] as const,
};

// The badge's own words, so a search for what the operator sees ("reconciled",
// "discrepancies") finds the row.
const statusLabel = (row: ReconciliationReportRow) =>
    row.discrepancyTotal > 0 ? I18n.get('discrepancies') : I18n.get('Reconciled');

const matchesKeyword = (row: ReconciliationReportRow, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [row.id, row.channel, row.runDate, statusLabel(row)]
        .some(value => typeof value === 'string' && value.toLowerCase().includes(needle));
};

const toRow = (report: ReconciliationReport): ReconciliationReportRow => ({
    ...report,
    discrepancyTotal: totalDiscrepancies(report),
});

export const useReconciliationReports = () => {
    const {channel} = useReconciliationReportsChannel();
    const {keyword, handleFilterByKeyword} = useReconciliationReportsSearch();

    // Sort is how one operator is viewing the table right now: component state, not
    // the module store (§3a).
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<ReconciliationReportRow>>(DEFAULT_SORT_STATUS);

    // Only what the server filters on is in the key: the channel, the GSI partition key.
    // Switching the channel refetches. The keyword is applied client-side below, so
    // keying on it would refetch identical pages per keystroke.
    const reportsQuery = useInfiniteQuery({
        queryKey: reconciliationReportsKeys.list(channel),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({channel, limit: PAGE_SIZE, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        // Rows are written once a day by the ChannelReconciler, never by this UI; 30s
        // stops a tab switch from refetching while Reload stays the way to see a new run.
        staleTime: 30_000,
    });

    // The service guarantees page.items is an array; the guard and filter(Boolean) keep
    // a malformed page or an undefined row out of the table.
    const items: ReconciliationReportRow[] = (reportsQuery.data?.pages?.flatMap(
        page => (page && 'items' in page && page.items) ? page.items : []) ?? [])
        .filter(Boolean)
        .map(toRow);

    const needle = keyword.trim().toLowerCase();

    // ⚠️ Client-side filter and sort cover the pages FETCHED SO FAR. Under token
    // pagination the browser cannot order or search runs it has not loaded; that is a
    // property of the model, not a bug (§9).
    const visibleItems = sortRecords(items.filter(row => matchesKeyword(row, needle)), sortStatus);

    const lastPage = reportsQuery.data?.pages?.[reportsQuery.data.pages.length - 1];

    return {
        // The loaded set BEFORE keyword filtering: what the footer's "N of M loaded"
        // is measured against.
        items,
        visibleItems,
        keyword,
        handleFilterByKeyword,
        sortStatus,
        setSortStatus,
        loading: reportsQuery.isLoading,
        isError: reportsQuery.isError,
        isFetchingMore: reportsQuery.isFetchingNextPage,
        // The server's own cursor, opaque to this client (§9), never a stand-in.
        nextToken: reportsQuery.hasNextPage ? (lastPage?.nextToken ?? null) : null,
        fetchRecords: () => {
            if (reportsQuery.hasNextPage && !reportsQuery.isFetchingNextPage) {
                reportsQuery.fetchNextPage();
            }
        },
        reload: () => {
            reportsQuery.refetch();
        },
    };
};
